/**
 * ReportViewerPage — `/reporting/reports/:id`.
 *
 * Renders a single ReportRun by id. Wired against:
 *   GET  /v1/reporting/runs/:id            — fetch run
 *   POST /v1/reporting/runs/:id/export     — export run as CSV/JSON (real)
 *
 * The result panel applies a typed renderer:
 *   - { rows: Object[] } → tabular renderer
 *   - { totalAmountMinor / totalCount / value } → single-value KPI
 *   - everything else → JSON pretty-print
 */
import { type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, FileBarChart } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  exportReportRun,
  getReportRun,
  type ExportResponseWire,
  type ReportRunWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

function ResultRenderer({ result }: { result: Record<string, unknown> | null }): JSX.Element {
  if (!result || typeof result !== 'object') {
    return <p className="text-sm text-muted py-4 text-center">No result data.</p>;
  }
  // Tabular renderer
  const rows = (result as { rows?: unknown }).rows;
  if (Array.isArray(rows) && rows.length > 0 && typeof rows[0] === 'object' && rows[0] !== null) {
    const headers = Object.keys(rows[0] as Record<string, unknown>);
    return (
      <div className="overflow-x-auto" data-testid="result-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
            <tr>
              {headers.map((h) => (
                <th key={h} className="text-left px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i}>
                {headers.map((h) => (
                  <td key={h} className="px-3 py-2 text-xs font-mono">
                    {String((r as Record<string, unknown>)[h] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // Single value renderer
  const single = (result as Record<string, unknown>);
  const valueKey = ['totalAmountMinor', 'totalCount', 'value', 'totalItemCount'].find(
    (k) => typeof single[k] === 'number',
  );
  if (valueKey !== undefined) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center" data-testid="result-single-value">
        <p className="text-[10px] text-muted uppercase tracking-wider mb-2">{valueKey}</p>
        <p className="text-3xl font-bold text-nipost-green">
          {Number(single[valueKey]).toLocaleString('en-US')}
        </p>
      </div>
    );
  }
  // JSON fallback
  return (
    <pre className="p-3 bg-gray-50 border border-border rounded-lg text-[11px] font-mono overflow-x-auto" data-testid="result-json">
      {JSON.stringify(result, null, 2)}
    </pre>
  );
}

export function ReportViewerPage(): JSX.Element {
  const { id } = useParams();
  const runId = id ?? '';
  const [exportResult, setExportResult] = useState<ExportResponseWire | null>(null);

  const run = useQuery<ReportRunWire, Error>({
    queryKey: ['reporting', 'run', runId],
    queryFn: () => getReportRun(runId),
    enabled: runId.length > 0,
    retry: false,
  });

  const exportM = useMutation<ExportResponseWire, Error, 'CSV' | 'JSON'>({
    mutationFn: (format) => exportReportRun(runId, format),
    onSuccess: (data) => setExportResult(data),
  });

  function downloadExport(data: ExportResponseWire): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const mime = data.format === 'CSV' ? 'text/csv' : 'application/json';
    const blob = new Blob([data.payload], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${runId}.${data.format.toLowerCase()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const denied =
    run.isError && run.error instanceof ApiError &&
    (run.error.status === 401 || run.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Report viewer"
        description={runId.length > 0 ? `Run ${runId}` : 'No run id supplied.'}
        actions={
          <Link
            to={runId.length > 0 ? `/reporting/reports/${encodeURIComponent(runId)}/versions` : '#'}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light transition-colors"
          >
            Versions →
          </Link>
        }
      />

      {runId.length === 0 ? (
        <DeferredNotice title="Missing run id">
          <p>Open a specific report run from the runs list to view its results.</p>
        </DeferredNotice>
      ) : run.isPending ? (
        <p className="text-sm text-muted">Loading run…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/getRun</code> required.
        </DeferredNotice>
      ) : run.isError ? (
        <DeferredNotice title="Run not found or service temporarily unavailable">
          {run.error.message}
        </DeferredNotice>
      ) : (
        <>
          <div className="bg-white border border-border rounded-xl p-5 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileBarChart className="w-4 h-4 text-nipost-green" />
                {run.data.reportKey}
              </h3>
              <StatusBadge status={run.data.status} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Field label="Started"   value={run.data.startedAt} />
              <Field label="Completed" value={run.data.completedAt ?? '—'} />
              <Field label="Duration"  value={run.data.durationMs !== null ? `${run.data.durationMs} ms` : '—'} />
              <Field label="From cache" value={run.data.fromCache ? 'yes' : 'no'} />
            </div>
            {run.data.failureReason !== null ? (
              <p className="text-xs text-red-600">Failure: {run.data.failureReason}</p>
            ) : null}
          </div>

          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Result</h3>
              <div className="flex items-center gap-2">
                {(['CSV', 'JSON'] as const).map((fmt) => (
                  <button
                    key={fmt}
                    type="button"
                    onClick={() => exportM.mutate(fmt)}
                    disabled={exportM.isPending}
                    data-testid={`export-${fmt.toLowerCase()}-btn`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light disabled:opacity-50"
                  >
                    <Download className="w-3.5 h-3.5" /> {fmt}
                  </button>
                ))}
              </div>
            </div>
            <ResultRenderer result={run.data.result} />
            {exportM.isError && (
              <p className="mt-3 text-xs text-red-600">Export failed: {exportM.error.message}</p>
            )}
            {exportResult && (
              <div className="mt-4 bg-gray-50 border border-border rounded-lg p-3" data-testid="export-result">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-700">
                    Generated {exportResult.format} export ({exportResult.byteSize} bytes)
                  </p>
                  <button
                    type="button"
                    onClick={() => downloadExport(exportResult)}
                    className="text-xs font-semibold text-nipost-green hover:underline"
                  >
                    Download →
                  </button>
                </div>
                <pre className="text-[11px] font-mono overflow-x-auto max-h-40">
                  {exportResult.payload.slice(0, 1000)}{exportResult.payload.length > 1000 ? '…' : ''}
                </pre>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-[10px] text-muted uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm font-mono text-gray-900 break-all">{value}</p>
    </div>
  );
}
